/**
 * Pravidla množství skladu: ks = celé kusy; m = metráž (lze uříznout část kusu).
 */

import {
  formatMaterialQuantityLabel,
  isPieceContentLengthUnit,
  isWarehousePieceLengthUnit,
  parseMaterialUnit,
  roundMaterialUsageQuantity,
} from './material-quantity.util';

export type WarehouseQuantityOperation = 'transfer' | 'issue' | 'receipt';

const WHOLE_PIECE_USAGE_UNITS = new Set(['ks', 'kus', 'kusy', 'kpl', 'komplet']);

function formatQtyCs(n: number): string {
  return new Intl.NumberFormat('cs-CZ', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(n);
}

function sapPrefix(sapCode?: string | null): string {
  const sap = String(sapCode ?? '').trim();
  return sap ? `SAP ${sap}: ` : '';
}

/**
 * Legacy only: pokud jednotka stále je `ks (N m)`, zlomky = metry → ks.
 * Katalog už `ks (N m)` nepoužívá (metry = `m`, kusy = `ks`) — pro `m`/`ks` no-op.
 */
export function normalizePieceLengthConsumeToUsageKs(
  qty: number,
  unit: string | null | undefined,
): number {
  const raw = roundMaterialUsageQuantity(qty);
  if (!Number.isFinite(raw) || raw <= 0) return raw;
  const info = parseMaterialUnit(unit);
  if (info.kind !== 'piece_length' || info.pieceContentMeters == null) {
    return raw;
  }
  if (isWholePieceQuantity(raw)) {
    return raw;
  }
  return roundMaterialUsageQuantity(raw / info.pieceContentMeters);
}

/** Kusové — provozní MJ je celý kus. Metrové (`m`) jde uříznout. */
export function requiresWholePieceQuantity(unit: string | null | undefined): boolean {
  if (isWarehousePieceLengthUnit(unit)) return false;
  const info = parseMaterialUnit(unit);
  if (info.kind === 'piece_length') return false;
  const usage = (info.usageUnit || info.normalized || '').toLowerCase();
  return WHOLE_PIECE_USAGE_UNITS.has(usage);
}

export function isWholePieceQuantity(qty: number): boolean {
  if (!Number.isFinite(qty) || qty <= 0) return false;
  return Math.abs(qty - Math.round(qty)) <= 1e-6;
}

export function warehouseWholePiecesError(
  sapCode: string | null | undefined,
  quantity: number,
  operation: WarehouseQuantityOperation = 'transfer',
): string {
  const verb =
    operation === 'issue'
      ? 'vydávejte'
      : operation === 'receipt'
        ? 'přijímejte'
        : 'převádějte';
  return (
    `${sapPrefix(sapCode)}kusové materiály ${verb} v celých kusech (ks)` +
    (Number.isFinite(quantity) ? `, ne ${formatQtyCs(quantity)}` : '')
  );
}

export function warehouseMeterPieceRequiredError(
  sapCode: string | null | undefined,
  operation: WarehouseQuantityOperation = 'transfer',
): string {
  const action =
    operation === 'issue'
      ? 'výdej'
      : operation === 'receipt'
        ? 'příjem'
        : 'převod';
  return (
    `${sapPrefix(sapCode)}délkový materiál — vyberte konkrétní kus a zadejte metráž k ${action === 'převod' ? 'převodu' : action === 'výdej' ? 'výdeji' : 'příjmu'}`
  );
}

/**
 * Částečný odběr metrů z kusu. `null` = OK.
 */
export function warehouseMeterCutError(params: {
  sapCode?: string | null;
  quantity: number;
  available: number;
  operation?: WarehouseQuantityOperation;
}): string | null {
  const qty = Number(params.quantity);
  const available = Number(params.available);
  const sap = sapPrefix(params.sapCode);
  const verb =
    params.operation === 'issue'
      ? 'vydat'
      : params.operation === 'receipt'
        ? 'přijmout'
        : 'převést';

  if (!Number.isFinite(qty) || qty <= 0) {
    return `${sap}zadejte kladnou metráž k ${verb === 'převést' ? 'převodu' : verb === 'vydat' ? 'výdeji' : 'příjmu'}`;
  }
  if (!Number.isFinite(available) || available <= 0) {
    return `${sap}kus nemá volnou zásobu`;
  }
  if (qty > available + 1e-9) {
    return (
      `Nelze ${verb} ${formatQtyCs(qty)} m` +
      (params.sapCode ? ` (SAP ${String(params.sapCode).trim()})` : '') +
      ` — na kusu je volných ${formatQtyCs(available)} m`
    );
  }
  return null;
}

export function warehousePieceQuantityError(params: {
  sapCode?: string | null;
  unit?: string | null;
  quantity: number;
  available?: number | null;
  warehouseItemId?: string | null;
  operation?: WarehouseQuantityOperation;
  /** Převod délkových: kus musí být vybraný. */
  requireMeterPiecePin?: boolean;
}): string | null {
  const qty = Number(params.quantity);
  const unit = params.unit ?? null;
  const operation = params.operation ?? 'transfer';
  const sap = String(params.sapCode ?? '').trim() || null;

  if (!Number.isFinite(qty) || qty <= 0) {
    return `${sapPrefix(sap)}množství musí být > 0`;
  }

  if (isWarehousePieceLengthUnit(unit)) {
    if (params.requireMeterPiecePin && !String(params.warehouseItemId ?? '').trim()) {
      return warehouseMeterPieceRequiredError(sap, operation);
    }
    if (params.available != null && Number.isFinite(params.available)) {
      return warehouseMeterCutError({
        sapCode: sap,
        quantity: qty,
        available: params.available,
        operation,
      });
    }
    return null;
  }

  if (requiresWholePieceQuantity(unit) && !isWholePieceQuantity(qty)) {
    return warehouseWholePiecesError(sap, qty, operation);
  }

  if (
    params.available != null &&
    Number.isFinite(params.available) &&
    qty > params.available + 1e-9
  ) {
    const verb =
      operation === 'issue' ? 'vydat' : operation === 'receipt' ? 'přijmout' : 'převést';
    const have = formatMaterialQuantityLabel(params.available, unit, 'stock');
    return (
      `Nelze ${verb} ${formatMaterialQuantityLabel(qty, unit, 'usage')}` +
      (sap ? ` (SAP ${sap})` : '') +
      ` — volných ${have}`
    );
  }

  return null;
}

/** Zaokrouhlení metráže stejně jako material_usage (3 desetinná). */
export function normalizeWarehouseMeterQuantity(qty: number): number {
  return roundMaterialUsageQuantity(qty);
}

/** Řádek spotřeby (material_usage) pro validaci proti osobnímu skladu. */
export type MaterialUsageStockCutInput = {
  sapCode: string;
  quantity: number;
  warehouseItemId?: string | null;
  /** Katalogová / skladová MJ — když chybí, odvodí se ze stock položky. */
  unit?: string | null;
};

export type MaterialUsageStockItemInput = {
  id: string;
  productCode?: string | null;
  quantityAvailable: number;
  unit?: string | null;
};

function consumeMeterFifoForSap(params: {
  sap: string;
  qty: number;
  stockItems: MaterialUsageStockItemInput[];
  remainingByPiece: Map<string, number>;
}): string | null {
  let need = params.qty;
  let available = 0;
  for (const item of params.stockItems ?? []) {
    const itemSap = String(item.productCode ?? '').trim();
    if (itemSap !== params.sap) continue;
    if (!isWarehousePieceLengthUnit(item.unit)) continue;
    const id = String(item.id ?? '').trim();
    if (!id) continue;
    const left = params.remainingByPiece.get(id) ?? 0;
    if (left <= 1e-9) continue;
    available += left;
    const take = Math.min(left, need);
    params.remainingByPiece.set(id, left - take);
    need = roundMaterialUsageQuantity(need - take);
    if (need <= 1e-9) return null;
  }
  return (
    `Nedostatečná zásoba na osobním skladu: ${params.sap} ` +
    `(potřeba ${formatQtyCs(params.qty)}, volné ${formatQtyCs(available)}).`
  );
}

/**
 * Validace spotřeby proti kusům osobního skladu.
 * Délkové `m`/`bm`: vyžaduje `warehouseItemId` a metráž ≤ zbývající na kusu.
 * Kusové: celá ks, součet per SAP ≤ agregát zásoby.
 * `null` = OK.
 */
export function validateMaterialUsageStockCuts(params: {
  items: MaterialUsageStockCutInput[];
  stockItems: MaterialUsageStockItemInput[];
  /** Default true — bez pinu délkový materiál odmítnout. */
  requireMeterPiecePin?: boolean;
  /**
   * Default true — zlomky u `ks (N m)` ber jako metry → ks.
   * Po `normalizePieceLengthConsumeToUsageKs` v material_usage nastavte `false`,
   * jinak 0.25 ks projde druhým dělením (0.125).
   */
  normalizeMeterInput?: boolean;
}): string | null {
  const requirePin = params.requireMeterPiecePin !== false;
  const normalizeMeterInput = params.normalizeMeterInput !== false;
  const stockById = new Map<string, MaterialUsageStockItemInput>();
  const remainingByPiece = new Map<string, number>();
  const availableBySap = new Map<string, number>();
  const unitBySap = new Map<string, string>();

  for (const item of params.stockItems ?? []) {
    const id = String(item.id ?? '').trim();
    const sap = String(item.productCode ?? '').trim();
    const avail = Number(item.quantityAvailable);
    if (id) {
      stockById.set(id, item);
      if (Number.isFinite(avail) && avail > 0) {
        remainingByPiece.set(id, (remainingByPiece.get(id) ?? 0) + avail);
      }
    }
    if (!sap || !Number.isFinite(avail) || avail <= 0) continue;
    availableBySap.set(sap, (availableBySap.get(sap) ?? 0) + avail);
    const unit = String(item.unit ?? '').trim();
    if (unit && !unitBySap.has(sap)) unitBySap.set(sap, unit);
  }

  const ksNeedBySap = new Map<string, number>();

  for (const line of params.items ?? []) {
    const sap = String(line.sapCode ?? '').trim();
    if (!sap) continue;

    const unit =
      String(line.unit ?? '').trim() ||
      unitBySap.get(sap) ||
      (line.warehouseItemId
        ? String(stockById.get(String(line.warehouseItemId).trim())?.unit ?? '').trim()
        : '');

    const qty =
      normalizeMeterInput && isPieceContentLengthUnit(unit)
        ? normalizePieceLengthConsumeToUsageKs(Number(line.quantity), unit)
        : roundMaterialUsageQuantity(Number(line.quantity));
    if (!Number.isFinite(qty) || qty <= 0) continue;

    if (isWarehousePieceLengthUnit(unit)) {
      const warehouseItemId = String(line.warehouseItemId ?? '').trim();
      if (requirePin && !warehouseItemId) {
        return warehouseMeterPieceRequiredError(sap, 'issue');
      }
      if (!warehouseItemId) continue;
      const piece = stockById.get(warehouseItemId);
      const remaining = remainingByPiece.get(warehouseItemId) ?? 0;
      if (piece) {
        const pieceSap = String(piece.productCode ?? '').trim();
        if (pieceSap && pieceSap !== sap) {
          return `${sapPrefix(sap)}kus nepatří k tomuto SAP`;
        }
        const cutErr = warehouseMeterCutError({
          sapCode: sap,
          quantity: qty,
          available: remaining,
          operation: 'issue',
        });
        if (cutErr) return cutErr;
        remainingByPiece.set(warehouseItemId, remaining - qty);
        continue;
      }
      // Stale pin (lot already issued / deleted from GET stock). Android retries
      // with the consumed WarehouseItem.id → 400 looked like a crash. Cut leftover
      // meter pieces of the same SAP on this warehouse only (caller scopes stock).
      const fifoErr = consumeMeterFifoForSap({
        sap,
        qty,
        stockItems: params.stockItems,
        remainingByPiece,
      });
      if (fifoErr) return fifoErr;
      continue;
    }

    if (isPieceContentLengthUnit(unit)) {
      const warehouseItemId = String(line.warehouseItemId ?? '').trim();
      if (warehouseItemId) {
        const piece = stockById.get(warehouseItemId);
        if (!piece) {
          return `${sapPrefix(sap)}vybraný kus není na osobním skladu`;
        }
        const pieceSap = String(piece.productCode ?? '').trim();
        if (pieceSap && pieceSap !== sap) {
          return `${sapPrefix(sap)}kus nepatří k tomuto SAP`;
        }
        const remaining = remainingByPiece.get(warehouseItemId) ?? 0;
        const pieceErr = warehousePieceQuantityError({
          sapCode: sap,
          unit,
          quantity: qty,
          available: remaining,
          warehouseItemId,
          operation: 'issue',
        });
        if (pieceErr) return pieceErr;
        remainingByPiece.set(warehouseItemId, remaining - qty);
        continue;
      }
    }

    const pieceErr = warehousePieceQuantityError({
      sapCode: sap,
      unit: unit || 'ks',
      quantity: qty,
      operation: 'issue',
    });
    if (pieceErr) return pieceErr;
    ksNeedBySap.set(sap, (ksNeedBySap.get(sap) ?? 0) + qty);
  }

  for (const [sap, need] of ksNeedBySap) {
    const available = availableBySap.get(sap) ?? 0;
    if (need > available + 1e-9) {
      return (
        `Nedostatečná zásoba na osobním skladu: ${sap} ` +
        `(potřeba ${formatQtyCs(need)}, volné ${formatQtyCs(available)}).`
      );
    }
  }

  return null;
}
